import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { toast } from 'react-hot-toast';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Shield, 
  Clock, 
  Check, 
  Copy, 
  Stethoscope,
  Crown,
  Award,
  Lock
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const TeamPage = () => {
  const { user, currentUser } = useAuth();
  const { maxUsersLimit } = usePermissions();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', rol: 'profesional' });
  const [isInviting, setIsInviting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);
  
  // Calcular si se alcanzo el limite
  const currentUsersCount = members.length;
  const pendingInvitesCount = invites.length;
  const totalSlots = currentUsersCount + pendingInvitesCount;
  const isLimitReached = totalSlots >= maxUsersLimit;

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      setIsLoading(true);
      const [membersRes, invitesRes] = await Promise.all([
        api.get('/usuarios/'),
        api.get('/usuarios/invitaciones/')
      ]);
      setMembers(membersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      console.error('Error fetching team data:', error);
      toast.error('Error al cargar el equipo');
    } finally {
      setIsLoading(false);
    }
  };



  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setIsInviting(true);
    setLastInviteLink(null);

    try {
      const response = await api.post('/usuarios/invitaciones/', inviteForm);
      
      const uuid = response.data.uuid;
      const link = `${window.location.origin}/auth/join/${uuid}`;
      setLastInviteLink(link);
      
      toast.success('¡Invitación creada con éxito!');
      fetchTeamData();
    } catch (error) {
      console.error('Invite error:', error);
      const errorMsg = error.response?.data?.error || 'Error al enviar invitación';
      
      if (errorMsg.includes('límite')) {
        toast.error(
          <div className="flex flex-col">
            <span className="font-bold">¡Límite del Plan Alcanzado!</span>
            <span className="text-sm">Actualiza a Premium para agregar más miembros.</span>
          </div>,
          { duration: 5000, icon: '🔒' }
        );
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsInviting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Enlace copiado al portapapeles');
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"><Crown className="w-3 h-3 mr-1" /> Admin</span>;
      case 'profesional':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><Stethoscope className="w-3 h-3 mr-1" /> Profesional</span>;
      case 'recepcionista':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><Users className="w-3 h-3 mr-1" /> Recepción</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{role}</span>;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Equipo</h1>
          <p className="text-gray-500 mt-1">Administra los miembros de tu clínica y sus permisos.</p>
        </div>
        <div className="flex gap-3">
          {/* Botón de invitar - condicional según límite */}
          {isLimitReached ? (
            <Link to="/app/suscripcion">
              <button
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-500 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
              >
                <Lock className="w-4 h-4 mr-2" />
                Límite alcanzado ({totalSlots}/{maxUsersLimit})
              </button>
            </Link>
          ) : (
            <button
              onClick={() => {
                setInviteForm({ email: '', rol: 'profesional' });
                setLastInviteLink(null);
                setShowInviteModal(true);
              }}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-[#2A9D8F] hover:bg-[#21867a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F] transition-colors"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invitar Nuevo Miembro
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Active Members List */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Users className="w-5 h-5 mr-2 text-[#2A9D8F]" />
              Miembros Activos
            </h2>
            <span className="bg-[#2A9D8F]/10 text-[#2A9D8F] py-1 px-3 rounded-full text-xs font-medium">
              {members.length} usuarios
            </span>
          </div>
          
          <div className="divide-y divide-gray-100">
            {isLoading ? (
              <div className="p-8 text-center text-gray-400">Cargando miembros...</div>
            ) : members.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No hay miembros aún.</div>
            ) : (
              members.map((member) => (
                <div key={member.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between group">
                  <div className="flex items-center space-x-4">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#2A9D8F] to-[#264653] flex items-center justify-center text-white font-medium shadow-sm">
                      {member.first_name?.[0]}{member.last_name?.[0] || member.email[0].toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {member.first_name} {member.last_name}
                        {member.id === (user?.id || currentUser?.id) && <span className="ml-2 text-xs text-gray-400">(Tú)</span>}
                      </h3>
                      <div className="flex items-center text-xs text-gray-500 mt-0.5">
                        <Mail className="w-3 h-3 mr-1" />
                        {member.email}
                      </div>
                      {member.id === (user?.id || currentUser?.id) && member.titulo_profesional && (
                        <div className="flex items-center text-xs text-[#2A9D8F] mt-1">
                          <Award className="w-3 h-3 mr-1" />
                          {member.titulo_profesional}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getRoleBadge(member.rol)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending Invitations */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-full">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Mail className="w-5 h-5 mr-2 text-orange-500" />
              Invitaciones Pendientes
            </h2>
          </div>
          
          <div className="flex-1 divide-y divide-gray-100 overflow-y-auto max-h-[500px]">
            {isLoading ? (
               <div className="p-8 text-center text-gray-400">Cargando...</div>
            ) : invites.length === 0 ? (
              <div className="p-8 text-center flex flex-col items-center justify-center h-full text-gray-400">
                <div className="bg-gray-100 p-3 rounded-full mb-3">
                  <Mail className="w-6 h-6 text-gray-400" />
                </div>
                <p>No hay invitaciones pendientes</p>
              </div>
            ) : (
              invites.map((invite) => (
                <div key={invite.uuid} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-gray-900 truncate max-w-[180px]" title={invite.email}>
                      {invite.email}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                      Pendiente
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <div className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      Expira: {new Date(invite.expira_en).toLocaleDateString()}
                    </div>
                    {getRoleBadge(invite.rol)}
                  </div>

                  <button 
                    onClick={() => copyToClipboard(`${window.location.origin}/auth/join/${invite.uuid}`)}
                    className="w-full flex items-center justify-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Copy className="w-3 h-3 mr-1.5" />
                    Copiar Enlace
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowInviteModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-teal-100 sm:mx-0 sm:h-10 sm:w-10">
                    <UserPlus className="h-6 w-6 text-[#2A9D8F]" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Invitar Nuevo Miembro
                    </h3>
                    <div className="mt-2 text-sm text-gray-500">
                      <p>Envía una invitación para unirse a tu clínica. El enlace expira en 48 horas.</p>
                    </div>

                    {!lastInviteLink ? (
                      <form onSubmit={handleInviteSubmit} className="mt-5 space-y-4">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            Correo Electrónico
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Mail className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              type="email"
                              name="email"
                              id="email"
                              required
                              className="focus:ring-[#2A9D8F] focus:border-[#2A9D8F] block w-full pl-10 sm:text-sm border-gray-300 rounded-lg p-2.5 bg-gray-50"
                              placeholder="colaborador@clinica.com"
                              value={inviteForm.email}
                              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="rol" className="block text-sm font-medium text-gray-700">
                            Rol Asignado
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Shield className="h-5 w-5 text-gray-400" />
                            </div>
                            <select
                              id="rol"
                              name="rol"
                              className="focus:ring-[#2A9D8F] focus:border-[#2A9D8F] block w-full pl-10 sm:text-sm border-gray-300 rounded-lg p-2.5 bg-gray-50"
                              value={inviteForm.rol}
                              onChange={(e) => setInviteForm({ ...inviteForm, rol: e.target.value })}
                            >
                              <option value="profesional">Profesional Clínico</option>
                              <option value="recepcionista">Recepcionista</option>
                              <option value="admin">Administrador</option>
                            </select>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {inviteForm.rol === 'admin' ? 'Acceso total a configuración y facturación.' : 
                             inviteForm.rol === 'profesional' ? 'Acceso a citas, fichas y pacientes.' : 
                             'Solo gestión de citas y agenda.'}
                          </p>
                        </div>
                        
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                          <button
                            type="submit"
                            disabled={isInviting}
                            className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 bg-[#2A9D8F] text-base font-medium text-white hover:bg-[#21867a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F] sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isInviting ? 'Enviando...' : 'Enviar Invitación'}
                          </button>
                          <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F] sm:mt-0 sm:w-auto sm:text-sm transition-colors"
                            onClick={() => setShowInviteModal(false)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="mt-5">
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <Check className="h-5 w-5 text-green-400" aria-hidden="true" />
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-green-800">Invitación creada</h3>
                              <div className="mt-2 text-sm text-green-700">
                                <p>Copia el siguiente enlace y envíalo a tu colaborador.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-1 flex rounded-md shadow-sm">
                          <input
                            type="text"
                            readOnly
                            value={lastInviteLink}
                            className="focus:ring-[#2A9D8F] focus:border-[#2A9D8F] flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300 bg-gray-50 p-2"
                          />
                          <button
                            onClick={() => copyToClipboard(lastInviteLink)}
                            className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#2A9D8F] focus:border-[#2A9D8F]"
                          >
                            <Copy className="h-5 w-5 text-gray-400" />
                            <span>Copiar</span>
                          </button>
                        </div>

                        <div className="mt-5 sm:mt-6">
                          <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 bg-[#2A9D8F] text-base font-medium text-white hover:bg-[#21867a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F] sm:text-sm transition-colors"
                            onClick={() => {
                              setShowInviteModal(false);
                              setLastInviteLink(null);
                            }}
                          >
                            Entendido
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;

